import { PropsWithChildren, ReactElement, createContext, useContext, useEffect } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { theme as nextTheme } from '@synergy/theme-next';

import { backofficeContract } from './backoffice-contract';
import { components } from './components';
import { palette } from './palette';
import { typography } from './typography';

export const paloTheme = createTheme({
    palette,
    typography,
    components,
    shape: { borderRadius: 6 },
    ...backofficeContract,
});

/**
 * The PALO look for the screens that are built on components-next.
 *
 * Those components need the theme they were written for, so the screens using
 * them wrap themselves in the next theme — which is the shop's, down to its
 * accent (#2196F3 against PALO's #1565D8), its chip and its table. Under a PALO
 * frame that reads as a different program: the import list, the configuration,
 * the mapping and the bonus settlement all show the shop's pager, chips and
 * blue inside a PALO canvas.
 *
 * This keeps every component override the next theme brings and lays the PALO
 * palette, type scale and density over it. The shop keeps its own theme
 * untouched — it never renders through this one.
 */
export const paloNextTheme = createTheme(nextTheme, {
    palette,
    typography,
    components,
    shape: { borderRadius: 6 },
    ...backofficeContract,
});

/**
 * Marks the subtree as running under the PALO theme. Read it with
 * usePaloThemeGuard to catch content that ended up on the ambient theme.
 */
const PaloThemeMarker = createContext(false);

export const PaloThemeProvider = ({ children }: PropsWithChildren): ReactElement => (
    <PaloThemeMarker.Provider value={true}>
        <ThemeProvider theme={paloTheme}>{children}</ThemeProvider>
    </PaloThemeMarker.Provider>
);

const warned = new Set<string>();

/**
 * Warns once per component when it renders outside PaloThemeProvider. Theme
 * context comes from an ancestor, so a component can look correct in one editor
 * and legacy in another — a mistake neither the file nor a screenshot reveals.
 */
export const PaloThemeGuard = ({ name }: { name: string }): null => {
    usePaloThemeGuard(name);

    return null;
};

export const usePaloThemeGuard = (componentName: string): void => {
    const isPalo = useContext(PaloThemeMarker);

    useEffect(() => {
        if (isPalo || process.env['NODE_ENV'] === 'production' || warned.has(componentName)) {
            return;
        }
        warned.add(componentName);
        // console.error, not warn: browser tooling routinely filters warnings
        // out, and a guard nobody sees is worse than none.
        // eslint-disable-next-line no-console
        console.error(
            `[PALO] <${componentName}> renders outside PaloThemeProvider and will pick up the ambient theme. ` +
                'Wrap it, or wrap the body that hosts it.'
        );
    }, [isPalo, componentName]);
};

/**
 * Same marker as PaloThemeProvider, so the theme guard stays quiet: content
 * under this provider is on a PALO theme, only one that also speaks
 * components-next.
 */
export const PaloNextThemeProvider = ({ children }: PropsWithChildren): ReactElement => (
    <PaloThemeMarker.Provider value={true}>
        <ThemeProvider theme={paloNextTheme}>{children}</ThemeProvider>
    </PaloThemeMarker.Provider>
);
